import { handleElectronicItem } from 'src/electronic-items/business-logic/handle-electronic-item';
import { performOnlineItemAuthorizationItemSale } from 'src/online-items/authorize-online-items';
import { handleOnlineItemDeclinedOnItemSale } from 'src/online-items/business-logic/handle-online-item-decline';
import { handleAuthorizeOnlineItemId } from 'src/online-items/handle-online-items';
import { handleQuantityWithBarcodeForbidden } from 'src/quantity-forbbiden/business-logic/handle-quantity-with-barcode-forbbiden';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { EmeraldError } from '../../common/api/emerald-error';
import { PriceEntryType } from '../../common/enumerations/price-entry-type';
import {
  ElectronicItemExternalIdRequired,
  ItemNotFound,
  ItemStatusLockedForSaleException,
  OnlineItemActivationDeclined,
  OnlineItemExternalIdRequired,
  OnlineItemQuantityForbidden,
  OnlineItemSaleAuthorization,
  PriceEntryRequired,
  PriceVerifyRequired,
  QuantityEntryRequired,
  QuantityWithBarcodeForbbiden,
} from '../../common/helpers/error-code';
import { IItemDetails } from '../../common/interfaces/dtos/IItemDetails';
import { IItemLookup } from '../../common/interfaces/dtos/IItemLookup';
import { handleForceQuantityItemSale } from '../../force-quantity/business-logic/handle-force-quantity';
import { handleItemNotFound } from '../../item-not-found/business-logic/handle-item-not-found';
import { ItemLockedForSaleErrorModal } from '../../locked-for-sale/business-logic/handle-item-locked-for-sale';
import { handleQuantityNotAllowedModal } from '../../online-items/handle-quantity-not-allowed';
import { handlePriceEntryExceptions } from '../../price-entry/business-logic/handle-price-entry-actions';
import { handlePriceVerifyRequiredExceptions } from '../../price-entry/business-logic/handle-price-verify-required';
import { ILineItemAddData } from './line-item-add';
import { SellingBehaviorType } from 'src/online-items/enumerations/OnlineItemAuthorizationTrigger';

export const handlableErrors = [
  QuantityEntryRequired,
  PriceEntryRequired,
  PriceVerifyRequired,
  ItemStatusLockedForSaleException,
  QuantityWithBarcodeForbbiden,
  ElectronicItemExternalIdRequired,
  OnlineItemExternalIdRequired,
  OnlineItemSaleAuthorization,
  OnlineItemQuantityForbidden,
];

export const handleLineItemError = async (
  lineItemData: ILineItemAddData,
  err: EmeraldError,
): Promise<void> => {
  const itemDetails: IItemDetails = {
    description: lineItemData.itemLookup.description || '',
    itemId: lineItemData.itemLookup.itemId || lineItemData.inputData.itemId || '',
    price: calculatePrice(lineItemData.itemLookup, lineItemData.lineItem.newPrice),
    quantity:
      lineItemData.itemLookup.regularSalesUnitPrice?.quantity ||
      lineItemData.inputData.quantity ||
      1,
    unitsOfMeasure: lineItemData.itemLookup.unitsOfMeasure
      ? `UOM_${lineItemData.itemLookup.unitsOfMeasure}`
      : 'UOM_EA',
  };

  let onlineItemGroup;

  const itemGroup = lineItemData.itemLookup.linkedItemsGroup?.groupCategory;
  const { behaviorLookupRequest } = getCurrentXLRConfig();
  if (behaviorLookupRequest && itemGroup && err.data) {
    onlineItemGroup = {
      authorizationExternalIdType: err.data.onlineItem?.authorizationExternalIdType,
      behaviorType: err.data.onlineItem ? SellingBehaviorType.OnlineItemType : '',
    };
  }

  switch (err.code) {
    case OnlineItemExternalIdRequired:
      return handleAuthorizeOnlineItemId(lineItemData, onlineItemGroup);
    case OnlineItemActivationDeclined:
      return handleOnlineItemDeclinedOnItemSale();
    case QuantityEntryRequired:
      return handleForceQuantityItemSale(lineItemData, itemDetails);
    case ItemNotFound:
      return handleItemNotFound(lineItemData.inputData);
    case PriceEntryRequired:
      return handlePriceEntryExceptions(PriceEntryType.PriceRequired, lineItemData, itemDetails);
    case PriceVerifyRequired:
      return handlePriceVerifyRequiredExceptions(
        PriceEntryType.PriceVerify,
        lineItemData,
        itemDetails,
      );
    case ItemStatusLockedForSaleException:
      throw ItemLockedForSaleErrorModal(lineItemData);
    case QuantityWithBarcodeForbbiden:
      throw handleQuantityWithBarcodeForbidden(lineItemData);
    case ElectronicItemExternalIdRequired:
      return handleElectronicItem(lineItemData);
    case OnlineItemSaleAuthorization:
      return performOnlineItemAuthorizationItemSale(
        lineItemData,
        lineItemData.lineItem.additionalData?.onlineItem?.authorizationExternalId,
      );
    case OnlineItemQuantityForbidden:
      throw handleQuantityNotAllowedModal();
    default:
      throw err;
  }
};

const calculatePrice = (itemLookup: IItemLookup, newPrice?: number): number => {
  let price: number = itemLookup.unitPrice || 0;

  if (newPrice) {
    price = newPrice;
  }

  if (itemLookup.depositItems) {
    let amountOfDeposits = 0;
    itemLookup.depositItems.forEach((x): void => {
      amountOfDeposits += x.amount;
    });
    let priceOfDeposits = amountOfDeposits;
    if (itemLookup.regularSalesUnitPrice) {
      priceOfDeposits = amountOfDeposits * (itemLookup.regularSalesUnitPrice.quantity || 1);
    }
    price += priceOfDeposits;
  }
  return price;
};
